import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, BadgeCheck, Siren, Facebook, Linkedin, Star } from "lucide-react";
import Logo from "./Logo";
import { SERVICES, PHONE, PHONE_TEL, EMAIL, EMAIL_MAILTO, ADDRESS, HOURS_WEEK, HOURS_TIME, AFTER_HOURS, SLOGAN, HEADER_SUNSET, FACEBOOK_URL, LINKEDIN_URL, GOOGLE_REVIEW_URL } from "@/lib/site";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-[#0D0D10] border-t border-gold/15">
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <img src={HEADER_SUNSET} alt="" className="w-full h-full object-cover object-bottom opacity-[0.22]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D10] via-[#0A0A0C]/70 to-[#0A0A0C]/40" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
        <div>
          <Logo />
          <p className="text-zinc-400 text-sm leading-relaxed mt-5">
            Faith-driven security technology and low-voltage service & repair for churches, schools, businesses, and homes across Middle Tennessee.
          </p>
          <div data-testid="footer-isnetworld-badge" className="inline-flex items-center gap-2 border border-gold/30 bg-gold/5 rounded-full px-4 py-2 mt-6">
            <BadgeCheck size={14} className="text-gold" />
            <span className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-gold-champagne">ISNetworld Member Contractor</span>
          </div>
          <div className="mt-6">
            <p className="font-mono-label text-[11px] uppercase tracking-[0.2em] text-gold-champagne mb-3">Like · Share · Follow!</p>
            <div className="flex items-center gap-3">
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook-link" aria-label="Redeemer Solutions on Facebook" className="w-9 h-9 rounded-sm border border-gold/25 flex items-center justify-center text-gold hover:bg-gold/10 hover:border-gold/50 transition-colors">
                <Facebook size={16} />
              </a>
              <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin-link" aria-label="Redeemer Solutions on LinkedIn" className="w-9 h-9 rounded-sm border border-gold/25 flex items-center justify-center text-gold hover:bg-gold/10 hover:border-gold/50 transition-colors">
                <Linkedin size={16} />
              </a>
              <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-review-link" className="inline-flex items-center gap-2 h-9 px-4 rounded-sm border border-gold/25 text-gold font-mono-label text-[10px] uppercase tracking-[0.16em] hover:bg-gold/10 hover:border-gold/50 transition-colors">
                <Star size={14} /> Leave us a review
              </a>
            </div>
          </div>
        </div>
        <div>
          <h3 className="font-display text-sm tracking-[0.2em] uppercase text-gold mb-5">Services</h3>
          <ul className="space-y-3 text-sm">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} data-testid={`footer-service-${s.slug}`} className="text-zinc-400 hover:text-gold-champagne transition-colors">
                  {s.shortName}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="font-display text-sm tracking-[0.2em] uppercase text-gold mb-5">Company</h3>
          <ul className="space-y-3 text-sm">
            <li><Link to="/about" data-testid="footer-about-link" className="text-zinc-400 hover:text-gold-champagne transition-colors">About Us</Link></li>
            <li><Link to="/why-choose-us" data-testid="footer-why-us-link" className="text-zinc-400 hover:text-gold-champagne transition-colors">Why Choose Us</Link></li>
            <li><Link to="/maintenance-plans" data-testid="footer-maintenance-link" className="text-zinc-400 hover:text-gold-champagne transition-colors">Maintenance Plans</Link></li>
            <li><Link to="/gallery" data-testid="footer-gallery-link" className="text-zinc-400 hover:text-gold-champagne transition-colors">Project Gallery</Link></li>
            <li><Link to="/testimonials" data-testid="footer-testimonials-link" className="text-zinc-400 hover:text-gold-champagne transition-colors">Testimonials</Link></li>
            <li><Link to="/request-quote" data-testid="footer-quote-link" className="text-zinc-400 hover:text-gold-champagne transition-colors">Request a Quote</Link></li>
            <li><Link to="/schedule" data-testid="footer-schedule-link" className="text-zinc-400 hover:text-gold-champagne transition-colors">Schedule Appointment</Link></li>
          </ul>
        </div>
        <div>
          <h3 className="font-display text-sm tracking-[0.2em] uppercase text-gold mb-5">Contact</h3>
          <ul className="space-y-4 text-sm text-zinc-400">
            <li>
              <a href={PHONE_TEL} data-testid="footer-phone-link" className="flex items-start gap-3 hover:text-gold-champagne transition-colors">
                <Phone size={15} className="text-gold mt-0.5 shrink-0" /> {PHONE}
              </a>
            </li>
            <li>
              <a href={EMAIL_MAILTO} data-testid="footer-email-link" className="flex items-start gap-3 hover:text-gold-champagne transition-colors break-all">
                <Mail size={15} className="text-gold mt-0.5 shrink-0" /> {EMAIL}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={15} className="text-gold mt-0.5 shrink-0" /> Christiana, TN 37037, USA
            </li>
            <li className="flex items-start gap-3">
              <Clock size={15} className="text-gold mt-0.5 shrink-0" />
              <span>{HOURS_WEEK} · {HOURS_TIME}<br /><span className="text-gold-champagne/80">{AFTER_HOURS}</span></span>
            </li>
            <li>
              <a href={PHONE_TEL} data-testid="footer-emergency-link" className="flex items-start gap-3 text-red-400 hover:text-red-300 transition-colors">
                <Siren size={15} className="mt-0.5 shrink-0" /> 24/7 Emergency Dispatch
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="gold-hairline relative" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-6 pb-24 md:py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
        <span>© {new Date().getFullYear()} Redeemer Solutions LLC · Christiana, Tennessee</span>
        <Link to="/admin" data-testid="footer-owner-link" aria-label="Owner login" title="There's always a solution." className="font-display tracking-[0.14em] text-gold/70 hover:text-gold transition-colors">{SLOGAN}</Link>
      </div>
    </footer>
  );
}
